'use client';

// The fields of the "Size & financials" filter group, one component each.
// State stays in the sidebar; SizeFilterGroup hands its props through.
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover } from '@/components/ui/popover';
import {
  FacetCount,
  FIELD_LABEL_CLASS,
  FIELD_PLACEHOLDER_CLASS,
  FilterCheckbox,
  FilterLabel,
} from '../FilterControls';
import { RangeSliderFilter } from '../RangeSliderFilter';
import {
  ComboboxContent,
  ComboboxTrigger,
  FilterChip,
  OptionCheck,
} from './FilterFieldParts';
import type { SizeFilterGroupProps } from './SizeFilterGroup';

/** Revenue band, plus the "include companies without revenue" modifier. */
export function RevenueField({
  t,
  lockOf,
  staged,
  set,
  extents,
}: Pick<
  SizeFilterGroupProps,
  't' | 'lockOf' | 'staged' | 'set' | 'extents'
>) {
  const { revenueMin, revenueMax, revenueIncludeNull } = staged;
  return (
    <>
      <RangeSliderFilter
        label={
          <FilterLabel
            lock={lockOf('revenueMin')}
            className={FIELD_LABEL_CLASS}
          >
            {t('search.sidebar.revenue', { defaultValue: 'Revenue' })}
          </FilterLabel>
        }
        min={revenueMin}
        max={revenueMax}
        onChange={(min, max) => {
          set.revenueMin(min);
          set.revenueMax(max);
        }}
        maxPos={extents.revenue_max}
        employees={false}
        lock={lockOf('revenueMin')}
      />

      {/* Only meaningful once a revenue band is picked — on its own it is
          not a filter, and the backend ignores it. Shown only then so it
          doesn't read as an independent toggle. It is also not counted in
          `sizeCount`: it modifies the revenue filter rather than being one. */}
      {(revenueMin !== undefined || revenueMax !== undefined) && (
        <label className="flex items-start gap-2 cursor-pointer select-none -mt-0.5">
          <Checkbox
            className="mt-0.5"
            checked={revenueIncludeNull}
            disabled={!!lockOf('revenueMin')}
            onCheckedChange={(v) => set.revenueIncludeNull(v === true)}
          />
          <span className="text-xs text-muted-foreground leading-snug">
            {t('search.sidebar.revenueIncludeNull', {
              defaultValue: 'Also include companies with no revenue on file',
            })}
          </span>
        </label>
      )}
    </>
  );
}

/** Bilanzsumme — a peer of revenue, not a replacement. It is the broadest
 *  size axis the product has (822,065 companies vs revenue's 77,126) because
 *  most German filers publish a balance sheet without a full P&L, so a user
 *  who gets nothing from a revenue band will usually get results here. */
export function AktivaField({
  t,
  lockOf,
  staged,
  set,
  extents,
}: Pick<
  SizeFilterGroupProps,
  't' | 'lockOf' | 'staged' | 'set' | 'extents'
>) {
  const { aktivaMin, aktivaMax } = staged;
  return (
    <RangeSliderFilter
      label={
        <FilterLabel lock={lockOf('aktivaMin')} className={FIELD_LABEL_CLASS}>
          {t('search.sidebar.aktiva', {
            defaultValue: 'Balance sheet total',
          })}
        </FilterLabel>
      }
      min={aktivaMin}
      max={aktivaMax}
      onChange={(min, max) => {
        set.aktivaMin(min);
        set.aktivaMax(max);
      }}
      maxPos={extents.aktiva_max}
      employees={false}
      lock={lockOf('aktivaMin')}
    />
  );
}

export function ProfitField({
  t,
  lockOf,
  staged,
  set,
  extents,
}: Pick<
  SizeFilterGroupProps,
  't' | 'lockOf' | 'staged' | 'set' | 'extents'
>) {
  const { profitMin, profitMax } = staged;
  return (
    <RangeSliderFilter
      label={
        <FilterLabel lock={lockOf('profitMin')} className={FIELD_LABEL_CLASS}>
          {t('search.sidebar.profit', { defaultValue: 'Profit' })}
        </FilterLabel>
      }
      min={profitMin}
      max={profitMax}
      onChange={(min, max) => {
        set.profitMin(min);
        set.profitMax(max);
      }}
      maxPos={extents.profit_max}
      maxAbsNeg={Math.abs(extents.profit_min)}
      employees={false}
      lock={lockOf('profitMin')}
    />
  );
}

export function EmployeesField({
  t,
  lockOf,
  staged,
  set,
  extents,
}: Pick<
  SizeFilterGroupProps,
  't' | 'lockOf' | 'staged' | 'set' | 'extents'
>) {
  const { employeesMin, employeesMax } = staged;
  return (
    <RangeSliderFilter
      label={
        <FilterLabel
          lock={lockOf('employeesMin')}
          className={FIELD_LABEL_CLASS}
        >
          {t('search.sidebar.employees', { defaultValue: 'Employee Count' })}
        </FilterLabel>
      }
      min={employeesMin}
      max={employeesMax}
      onChange={(min, max) => {
        set.employeesMin(min);
        set.employeesMax(max);
      }}
      maxPos={extents.employees_max}
      employees={true}
      lock={lockOf('employeesMin')}
    />
  );
}

/** Financial year — single value; picking one closes the picker. Backed by
 *  `mv_search.financial_years @> ARRAY[Y]`, i.e. "the company filed a report
 *  covering Y". It used to compare against the single latest-filing year,
 *  which meant picking 2020 selected the ~31,021 companies that *stopped*
 *  filing in 2020 rather than the ~484,503 that filed for it. */
export function FinancialYearField({
  t,
  lockOf,
  yearPickerOpen,
  setYearPickerOpen,
  staged,
  set,
  yearOptions,
  facetCount,
}: Pick<
  SizeFilterGroupProps,
  | 't'
  | 'lockOf'
  | 'yearPickerOpen'
  | 'setYearPickerOpen'
  | 'staged'
  | 'set'
  | 'yearOptions'
  | 'facetCount'
>) {
  const { financialYear } = staged;
  return (
    <div className="space-y-1.5">
      <FilterLabel lock={lockOf('financialYear')} className={FIELD_LABEL_CLASS}>
        {t('search.sidebar.financialYear', {
          defaultValue: 'Has report for year',
        })}
      </FilterLabel>
      <Popover
        open={lockOf('financialYear') ? false : yearPickerOpen}
        onOpenChange={setYearPickerOpen}
      >
        <ComboboxTrigger disabled={!!lockOf('financialYear')}>
          {financialYear ? (
            <FilterChip onRemove={() => set.financialYear(undefined)}>
              <span className="font-mono">{financialYear}</span>
            </FilterChip>
          ) : (
            <span className={FIELD_PLACEHOLDER_CLASS}>
              {t('search.sidebar.selectYear')}
            </span>
          )}
        </ComboboxTrigger>
        <ComboboxContent>
          <Command>
            <CommandList className="max-h-[272px]">
              <CommandGroup>
                {yearOptions.map((y) => (
                  <CommandItem
                    key={y}
                    value={String(y)}
                    onSelect={() => {
                      set.financialYear(y);
                      setYearPickerOpen(false);
                    }}
                  >
                    <OptionCheck selected={financialYear === y} />
                    <span className="font-mono text-xs">{y}</span>
                    <FacetCount
                      count={facetCount('financial_year', String(y))}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </ComboboxContent>
      </Popover>
    </div>
  );
}

/** Filings-only toggle — backed by hasYearlyMetadata. Useful as a quick
 *  "only show companies with financials" gate when the user cares about
 *  revenue/profit comparability. */
export function HasFilingsField({
  t,
  lockOf,
  staged,
  set,
  facetCount,
}: Pick<
  SizeFilterGroupProps,
  't' | 'lockOf' | 'staged' | 'set' | 'facetCount'
>) {
  const { hasYearlyMetadata } = staged;
  return (
    <FilterCheckbox
      checked={hasYearlyMetadata}
      onCheckedChange={set.hasYearlyMetadata}
      lock={lockOf('hasYearlyMetadata')}
      label={t('search.sidebar.hasYearlyMetadata', {
        defaultValue: 'Only companies with filings',
      })}
      count={facetCount('has_filings', 'true')}
    />
  );
}
